import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import type { Session } from "../../../entities/session/session";
import { resolveApiBaseUrl } from "../../../shared/api/client";
import {
  anonymousSession,
  memberQueryKeys,
  purgeAdminIdentity,
  purgeMemberIdentity,
  sessionQueryOptions,
  SessionExpiredError,
  SessionRequestError,
} from "./session-query";

interface SessionContextValue {
  readonly data: Session | undefined;
  readonly error: SessionRequestError | null;
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly isLoggingOut: boolean;
  readonly isPending: boolean;
  readonly logout: () => Promise<void>;
  readonly refetch: UseQueryResult<
    Session,
    SessionRequestError
  >["refetch"];
}

const SessionContext = createContext<SessionContextValue | null>(null);
const maximumTimerDelay = 2_147_483_647;

function authenticatedSessionKey(session: Session | undefined): string | null {
  return session?.authenticated === true
    ? `${session.user.id}\u0000${session.expires_at}`
    : null;
}

function logoutUrl(): string {
  return `${resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL).replace(/\/$/u, "")}/auth/logout`;
}

export function SessionProvider({ children }: PropsWithChildren) {
  const query = useQuery(sessionQueryOptions());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mounted = useRef(true);
  const inFlightLogout = useRef<Promise<void> | null>(null);
  const observedUserId = useRef<string | null | undefined>(undefined);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [expiredSessionKey, setExpiredSessionKey] =
    useState<string | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const activeSessionKey = authenticatedSessionKey(query.data);
  const expiredByClock =
    activeSessionKey !== null && expiredSessionKey === activeSessionKey;
  const sessionExpired =
    expiredByClock || query.error instanceof SessionExpiredError;
  const effectiveError = useMemo(
    () =>
      expiredByClock
        ? new SessionExpiredError()
        : query.error,
    [expiredByClock, query.error],
  );
  useEffect(() => {
    if (
      isLoggingOut ||
      activeSessionKey === null ||
      query.data?.authenticated !== true ||
      query.error instanceof SessionExpiredError ||
      expiredByClock
    ) {
      return;
    }

    const expiresAt = Date.parse(query.data.expires_at);
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const scheduleExpiration = () => {
      if (cancelled) {
        return;
      }
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) {
        setExpiredSessionKey(activeSessionKey);
        return;
      }
      timeout = globalThis.setTimeout(
        scheduleExpiration,
        Math.min(remaining, maximumTimerDelay),
      );
    };

    scheduleExpiration();
    return () => {
      cancelled = true;
      if (timeout !== undefined) {
        globalThis.clearTimeout(timeout);
      }
    };
  }, [
    activeSessionKey,
    expiredByClock,
    isLoggingOut,
    query.data,
    query.error,
  ]);
  const currentUserId =
    !sessionExpired && query.data?.authenticated === true
      ? query.data.user.id
      : null;
  useEffect(() => {
    if (isLoggingOut) {
      return;
    }
    const previousUserId = observedUserId.current;
    observedUserId.current = currentUserId;
    if (
      previousUserId === undefined ||
      previousUserId === currentUserId ||
      previousUserId === null
    ) {
      return;
    }
    void Promise.all([
      purgeMemberIdentity(queryClient, previousUserId),
      purgeAdminIdentity(queryClient),
    ]);
  }, [currentUserId, isLoggingOut, queryClient]);

  const logout = useCallback((): Promise<void> => {
    if (inFlightLogout.current) {
      return inFlightLogout.current;
    }

    const previousSession =
      queryClient.getQueryData<Session>(memberQueryKeys.session);
    setIsLoggingOut(true);
    queryClient.setQueryData(memberQueryKeys.session, anonymousSession);

    const request = (async () => {
      try {
        await queryClient.cancelQueries({
          queryKey: memberQueryKeys.all,
        });
        await purgeAdminIdentity(queryClient);
        const response = await fetch(logoutUrl(), {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
        });
        if (response.status !== 204) {
          throw new SessionRequestError(
            "O servidor não confirmou o encerramento da sessão.",
            { status: response.status },
          );
        }

        queryClient.removeQueries({
          queryKey: memberQueryKeys.all,
        });
        queryClient.removeQueries({
          queryKey: ["admin"],
        });
        queryClient.setQueryData(
          memberQueryKeys.session,
          anonymousSession,
        );
        if (mounted.current) {
          void navigate("/entrar");
        }
      } catch (error) {
        if (previousSession) {
          queryClient.setQueryData(
            memberQueryKeys.session,
            previousSession,
          );
        } else {
          queryClient.removeQueries({
            queryKey: memberQueryKeys.session,
            exact: true,
          });
        }
        throw error;
      } finally {
        inFlightLogout.current = null;
        if (mounted.current) {
          setIsLoggingOut(false);
        }
      }
    })();

    inFlightLogout.current = request;
    return request;
  }, [navigate, queryClient]);

  const value = useMemo<SessionContextValue>(
    () => ({
      data:
        isLoggingOut || sessionExpired
          ? anonymousSession
          : query.data,
      error: effectiveError,
      isError: sessionExpired || query.isError,
      isFetching: query.isFetching,
      isLoggingOut,
      isPending: query.isPending,
      logout,
      refetch: query.refetch,
    }),
    [
      isLoggingOut,
      logout,
      query.data,
      effectiveError,
      query.isError,
      query.isFetching,
      query.isPending,
      query.refetch,
      sessionExpired,
    ],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error("useSession must be used inside SessionProvider.");
  }
  return session;
}

export function useAuthenticatedMemberId(): string {
  const session = useSession();
  if (!session.data?.authenticated) {
    throw new Error(
      "useAuthenticatedMemberId must be used behind MemberGuard.",
    );
  }
  return session.data.user.id;
}
